import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { LoginResponse } from '@aiai/shared';
import { getToken, setToken } from './token';
import { getSlot, adoptSlotOwner, handleOwnerChanged } from './accountSlot';
import { getActiveOrgId, getActiveOrgSlug } from './activeOrg';

// Paths that are NOT workspace-scoped: auth, the workspace list itself,
// first-run setup, health/version, invite acceptance (pre-membership), the
// external MCP endpoint and its account-level OAuth consent / connected apps,
// and the public prototype-feedback ingest. Everything else is an org resource
// and gets the `/w/<slug>` prefix.
const GLOBAL_API_PREFIXES = [
  '/auth',
  '/orgs',
  // The cross-workspace dashboard aggregates across all the caller's
  // workspaces, so it is deliberately not scoped to one.
  '/me',
  '/setup',
  '/health',
  '/version',
  '/invitations',
  '/mcp',
  '/oauth',
  '/prototype-feedback',
];

function isWorkspaceScopedPath(url: string): boolean {
  if (!url.startsWith('/') || url.startsWith('/w/')) return false;
  return !GLOBAL_API_PREFIXES.some(
    (p) => url === p || url.startsWith(`${p}/`) || url.startsWith(`${p}?`),
  );
}

/**
 * In dev, VITE_API_URL is unset and requests go to '/api', proxied to :3000.
 * In production the app is served same-origin by the API/Caddy, so '' works too.
 */
const API_BASE = import.meta.env.VITE_API_URL ?? '';

// `withCredentials` so the HttpOnly refresh-token cookie (Path=/api/auth) is
// sent to /auth/refresh and /auth/logout, and stored from Set-Cookie on
// login/setup. It's a no-op for every other route since the cookie's Path
// scopes it to /api/auth only.
export const http: AxiosInstance = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// A separate instance for the refresh call itself: it must never carry a
// stale bearer token or recurse through the 401 handler below.
const refreshClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// Attach bearer token to every request when present.
http.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// Workspace-scope every org request via the URL: `/issues/RV-51` becomes
// `/w/<slug>/issues/RV-51`, so the API call reads identically to the browser
// route. The X-Org-Id header stays as a belt-and-suspenders fallback — if a
// path were ever missed here, the server still scopes it correctly rather than
// leaking or 404ing. Global endpoints (/auth, /orgs, /me, …) are left bare.
http.interceptors.request.use((config) => {
  const slug = getActiveOrgSlug();
  const url = config.url ?? '';
  if (slug && isWorkspaceScopedPath(url)) {
    config.url = `/w/${slug}${url}`;
  }
  const orgId = getActiveOrgId();
  if (orgId) {
    config.headers.set('X-Org-Id', orgId);
  }
  return config;
});

let onUnauthorized: (() => void) | null = null;

/** Allow the auth layer to react to forced logout (clear state + redirect). */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

function isAuthEndpoint(url?: string): boolean {
  return !!url && (url.includes('/auth/login') || url.includes('/auth/refresh'));
}

let refreshPromise: Promise<string | null> | null = null;

/**
 * Exchange the HttpOnly refresh cookie for a new access token. Concurrent
 * callers (e.g. several requests 401-ing at once) share one in-flight
 * request rather than each hammering the endpoint.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = refreshClient
      .post<LoginResponse>('/auth/refresh', undefined, { params: { u: getSlot() } })
      .then((res) => {
        if (!adoptSlotOwner(res.data.user.id)) {
          setToken(null);
          handleOwnerChanged();
          return null;
        }
        setToken(res.data.accessToken);
        return res.data.accessToken;
      })
      .catch(() => {
        setToken(null);
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

type RetriableConfig = InternalAxiosRequestConfig & { _retriedAfterRefresh?: boolean };

// On 401: try one silent refresh + retry before giving up. Only a failed
// refresh (or a 401 on an auth endpoint itself) triggers the forced-logout
// handler, so a merely-expired access token no longer nukes the run view.
http.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;
    if (error.response?.status !== 401 || !config || isAuthEndpoint(config.url)) {
      return Promise.reject(error);
    }
    if (config._retriedAfterRefresh) {
      onUnauthorized?.();
      return Promise.reject(error);
    }
    config._retriedAfterRefresh = true;
    const newToken = await refreshAccessToken();
    if (!newToken) {
      onUnauthorized?.();
      return Promise.reject(error);
    }
    config.headers.set('Authorization', `Bearer ${newToken}`);
    return http(config);
  },
);

export { errorMessage } from './errorMessage';
